import {
    bundleIcon,
    CheckmarkFilled,
    CheckmarkRegular,
    DismissFilled,
    DismissRegular,
    QuestionFilled,
    QuestionRegular,
} from '@fluentui/react-icons'
import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import useAxios from '../../hooks/useAxios'
import { InvoiceApprovalStatus } from './InvoiceApprovalOverview'
import PersonAvatar from '../../components/PersonAvatar'
import { Button, mergeClasses, Spinner, Textarea, Tooltip } from '@fluentui/react-components'
import { useBoolean } from '@fluentui/react-hooks'
import useUsers from '../../hooks/useUsers'
import { InvoiceApprovalStatusLabels } from '../../components/TagPicker/InvoiceApprovalStatusPicker'
import { useTranslation } from 'react-i18next'
import {
    InvoiceApprovalMutationDto,
    useInvoiceApprovalStyles,
    UserInvoiceApprovalMutationDto,
    ViewInvoiceApprovalComponentProps,
} from './ViewInvoiceApproval'
import useUser from '../../hooks/useUser'
import { formatNumber, formatTimeInMinutes, parseNumber, parseTimeInMinutes } from '../../helpers/numberHelper'
import MoneyText from '../../components/Text/Currency/MoneyText'
import TimeText from '../../components/Text/Time/TimeText'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useFormikContext } from 'formik'
import { useHistory } from 'react-router-dom'
import { ExtendedDisbursementOverviewDto } from '../Disbursement/BaseDisbursementOverview'
import { ExtendedFixedPriceOverviewDto } from '../FixedPrice/BaseFixedPriceOverview'
import { ExtendedTimeOverviewDto } from '../Time/BaseTimeOverview'
import { ExtendedTravelOverviewDto } from '../Travel/BaseTravelOverview'
import DistanceText from '../../components/Text/Distance/DistanceText'

const CheckmarkIcon = bundleIcon(CheckmarkFilled, CheckmarkRegular)
const QuestionIcon = bundleIcon(QuestionFilled, QuestionRegular)
const DismissIcon = bundleIcon(DismissFilled, DismissRegular)

type ViewUserInvoiceApprovalProps = ViewInvoiceApprovalComponentProps & {
    index: number
    isApprovalActive: boolean
    isFileOwnerOrAdmin: boolean
    userApproval: UserInvoiceApprovalMutationDto
    selectedDisbursements: ExtendedDisbursementOverviewDto[]
    selectedFixedPrices: ExtendedFixedPriceOverviewDto[]
    selectedTimes: ExtendedTimeOverviewDto[]
    selectedTravels: ExtendedTravelOverviewDto[]
}

export function UserInvoiceApproval(props: ViewUserInvoiceApprovalProps): ReactElement {
    const axios = useAxios()
    const history = useHistory()
    const styles = useInvoiceApprovalStyles()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()
    const { getUserById } = useUsers()
    const { viewport, isViewport } = useViewport()

    const { setFieldValue } = useFormikContext<InvoiceApprovalMutationDto>()

    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    const [submitting, { setTrue: setSubmitting, setFalse: setNotSubmitting }] = useBoolean(false)
    const [currentStatus, setCurrentStatus] = useState<InvoiceApprovalStatus>(props.userApproval.status)

    const isUserApprovalOwner = useMemo(() => currentUser.id === props.userApproval.userId, [currentUser])
    const isButtonDisabled = useMemo(
        () =>
            ![InvoiceApprovalStatus.New, InvoiceApprovalStatus.Active, InvoiceApprovalStatus.Approved].includes(
                props.approvalStatus,
            ) ||
            (!props.isFileOwnerOrAdmin && !isUserApprovalOwner),
        [props.approvalStatus],
    )

    const totalUserTime = useMemo(
        () =>
            formatTimeInMinutes(
                props.selectedTimes
                    .filter(item => item.user.id === props.userApproval.userId)
                    .map(item => parseTimeInMinutes(item.duration))
                    .reduce((a, b) => a + b, 0) || 0,
            ),
        [props.selectedTimes],
    )
    const totalUserDistance = useMemo(
        () =>
            formatNumber(
                props.selectedTravels
                    .filter(item => item.user.id === props.userApproval.userId)
                    .map(item => parseNumber(item.distance, 'en'))
                    .reduce((a, b) => a + b, 0) || 0,
                '0.0',
                'en',
            ),
        [props.selectedTimes],
    )
    const totalUserRate = useMemo(
        () =>
            props.selectedTimes
                .filter(item => item.user.id === props.userApproval.userId)
                .map(item => item.turnover.amount)
                .concat(props.selectedTravels.map(item => item.turnover.amount))
                .concat(props.selectedDisbursements.map(item => item.turnover.amount))
                .concat(props.selectedFixedPrices.map(item => item.turnover.amount))
                .map(amount => parseNumber(amount, 'en'))
                .reduce((a, b) => a + b, 0) || '0.00',
        [props.selectedTimes],
    )

    const toggleSubmitting = () => {
        setTimeout(setNotSubmitting, 500)
    }

    const commentToMutation = (
        userApproval: UserInvoiceApprovalMutationDto,
        comment: string,
        ownerComment: string,
    ): UserInvoiceApprovalMutationDto => {
        return { ...userApproval, userId: userApproval.userId, comment: comment, ownerComment: ownerComment }
    }

    const setStatus = (userApproval: UserInvoiceApprovalMutationDto, status: InvoiceApprovalStatus) => {
        if (userApproval.status === status) {
            return
        }

        setSubmitting()
        axios
            .post(`/finance/invoice-approval/user/${userApproval.id}/${status.toLowerCase()}`)
            .then(response => {
                setCurrentStatus(status)
                if (response.data?.status) {
                    props.setApprovalStatus(response.data?.status)
                }
            })
            .finally(() => {
                setFieldValue(`userApprovals[${props.index}].status`, status)
                toggleSubmitting()
                if (!props.isFileOwnerOrAdmin) {
                    history.push(`/invoice-approval`)
                }
            })
    }

    return (
        <div
            className={mergeClasses(
                styles.userValidationContainer,
                viewportSmall && styles.userValidationSmallContainer,
            )}
        >
            <div className={styles.centerItems}>
                <PersonAvatar name={getUserById(props.userApproval.userId)?.fullName} color="colorful" size={24} />
                <div className={styles.centerItems}>
                    <TimeText value={totalUserTime} />
                </div>
                <div className={styles.centerItems}>
                    <DistanceText value={totalUserDistance} unit="km" />
                </div>
                <div>
                    <MoneyText
                        alpha={props.meta.data?.files?.[0]?.currency || 'EUR'}
                        align={'center'}
                        amount={totalUserRate}
                    />
                </div>
            </div>
            <div className={styles.flexGap}>
                <Textarea
                    className={mergeClasses(styles.userValidationComment)}
                    placeholder={translate('invoiceapproval.userapprovals.comment.placeholder')}
                    defaultValue={props.userApproval.comment || ''}
                    resize={'both'}
                    disabled={isButtonDisabled}
                    onBlur={e => {
                        setSubmitting()
                        axios
                            .put(
                                `/finance/invoice-approval/user/${props.userApproval.id}`,
                                commentToMutation(props.userApproval, e.target.value, props.userApproval.ownerComment),
                            )
                            .finally(() => {
                                setFieldValue(`userApprovals[${props.index}].comment`, e.target.value)
                                toggleSubmitting()
                            })
                    }}
                />
                <Textarea
                    className={mergeClasses(styles.userValidationComment)}
                    placeholder={translate('invoiceapproval.userapprovals.ownercomment.placeholder')}
                    defaultValue={props.userApproval.ownerComment || ''}
                    resize={'both'}
                    disabled={!props.isFileOwnerOrAdmin || isButtonDisabled}
                    onBlur={e => {
                        setSubmitting()
                        axios
                            .put(
                                `/finance/invoice-approval/user/${props.userApproval.id}`,
                                commentToMutation(props.userApproval, props.userApproval.comment, e.target.value),
                            )
                            .finally(() => {
                                setFieldValue(`userApprovals[${props.index}].ownerComment`, e.target.value)
                                toggleSubmitting()
                            })
                    }}
                />
            </div>
            {viewportSmall && <div></div>}
            <div className={styles.userValidationActionContainer}>
                <div
                    className={mergeClasses(
                        styles.flexGap,
                        styles.flexWrap,
                        viewportSmall && styles.userValidationActionSmallContainer,
                    )}
                >
                    <Tooltip
                        relationship={'label'}
                        content={translate(InvoiceApprovalStatusLabels.get(InvoiceApprovalStatus.Approved).name)}
                        withArrow
                    >
                        <Button
                            icon={<CheckmarkIcon />}
                            disabled={isButtonDisabled}
                            onClick={() => setStatus(props.userApproval, InvoiceApprovalStatus.Approved)}
                            appearance={
                                currentStatus === InvoiceApprovalStatus.Approved
                                    ? 'primary'
                                    : isButtonDisabled
                                      ? 'transparent'
                                      : 'secondary'
                            }
                        />
                    </Tooltip>
                    <Tooltip
                        relationship={'label'}
                        content={translate(InvoiceApprovalStatusLabels.get(InvoiceApprovalStatus.Active).name)}
                        withArrow
                    >
                        <Button
                            icon={<QuestionIcon />}
                            disabled={isButtonDisabled}
                            onClick={() => setStatus(props.userApproval, InvoiceApprovalStatus.Active)}
                            appearance={
                                currentStatus === InvoiceApprovalStatus.Active
                                    ? 'primary'
                                    : isButtonDisabled
                                      ? 'transparent'
                                      : 'secondary'
                            }
                        />
                    </Tooltip>
                    <Tooltip
                        relationship={'label'}
                        content={translate(InvoiceApprovalStatusLabels.get(InvoiceApprovalStatus.Rejected).name)}
                        withArrow
                    >
                        <Button
                            icon={<DismissIcon />}
                            disabled={isButtonDisabled}
                            onClick={() => setStatus(props.userApproval, InvoiceApprovalStatus.Rejected)}
                            appearance={
                                currentStatus === InvoiceApprovalStatus.Rejected
                                    ? 'primary'
                                    : isButtonDisabled
                                      ? 'transparent'
                                      : 'secondary'
                            }
                        />
                    </Tooltip>
                    {viewportSmall && submitting && (
                        <Spinner label={translate('action.save')} labelPosition={'before'} size={'tiny'} />
                    )}
                </div>
                {!viewportSmall && submitting && (
                    <Spinner label={translate('action.save')} labelPosition={'before'} size={'tiny'} />
                )}
            </div>
        </div>
    )
}
